// Guest join flow against event-manager's public participant routes (proxied
// unauthenticated by the orchestrator BFF: `/api/events/slug/:slug/guest`, the join
// `POST /api/events/:id/participants`, and the guest's own
// `/api/events/:id/participants/:pid/**` routes):
//
//   look up the event → (recording notice, acknowledged) → request to join →
//   wait for admission → fetch the participant RIFT token → connect, and stay
//   connected until Leave.
//
// Contracts v2 §§6–7 (ADR-JSS-0012): a participant is `requested`, `approved`
// (admitted) or `removed`. An admitted guest holds its RIFT session,
// publishing and watching the return feed, whether or not the producer has
// put it on a slot; its own row's `slot` (1…N, or null) is what says it is
// live. A take only re-routes at the gateway, so it needs nothing from here
// but the keyframe the gateway asks for on flow 4 (guest-client.ts).
//
// Only Leave leaves. Closing the tab, losing the network or unmounting the
// page is not a Leave: the gateway reports the guest disconnected and
// event-manager removes them once the presence timeout runs out (§7), so a
// guest whose network blips keeps their slot. `dispose()` stops this
// controller without leaving; `leave()` is the Leave button.
//
// The join answers a `guestToken` once (contracts §1). It is the guest's only
// credential: every later call presents it in `X-Participant-Token`, and the
// guest reads its own status from `GET …/participants/:pid` (the list is the
// producers'). It lives in the JoinController's memory beside the participant
// id; a 401 on it means the participant is gone for this guest (e.g. it was
// created before guest tokens), so the guest must join again.
//
// The recording notice (ADR-JSS-0007 Decision 5, contracts §6) is shown
// before the guest can request to join whenever the event's public info says
// `recordParticipantIsos`, and again — blocking the connect — if the token
// response says `recordIso` and the guest has not yet acknowledged. The
// second check exists because the token is what the gateway acts on, and the
// operator can flip the flag between the lookup and the admission.
//
// The rift-token route (contracts §1, JSS#824) answers
// `{ token, expiresAt, recordIso }`; 400 = not admitted (yet, or any more),
// 404 = unknown event/participant, 409 = the event has no programme stream or
// no org — a setup fault no amount of retrying fixes.
/** Expiry in epoch ms: the response's `expiresAt`, else the JWT's `exp`. */
export function tokenExpiryMs(t) {
    const fromBody = Date.parse(t.expiresAt);
    if (Number.isFinite(fromBody))
        return fromBody;
    const exp = decodeTokenClaims(t.token).exp;
    return exp ? exp * 1000 : null;
}
export class HttpError extends Error {
    status;
    constructor(status, message) {
        super(message);
        this.status = status;
    }
}
/** The header a guest presents its token in (contracts §1). */
export const PARTICIPANT_TOKEN_HEADER = 'X-Participant-Token';
/** 401/403: the guest's token no longer opens its participant — join again. */
export function isGuestAuthError(err) {
    return err instanceof HttpError && (err.status === 401 || err.status === 403);
}
const guestHeaders = (guestToken) => ({ [PARTICIPANT_TOKEN_HEADER]: guestToken });
export class GuestApi {
    base;
    fetchFn;
    constructor(base = '/api', fetchFn = (i, init) => fetch(i, init)) {
        this.base = base;
        this.fetchFn = fetchFn;
    }
    async json(path, init) {
        const res = await this.fetchFn(`${this.base}${path}`, {
            ...init,
            headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
        });
        if (!res.ok) {
            let msg = `${res.status}`;
            try {
                const body = (await res.json());
                if (body.error)
                    msg = body.error;
            }
            catch {
                /* non-JSON error body */
            }
            throw new HttpError(res.status, msg);
        }
        return (await res.json());
    }
    /**
     * The guest projection of the event (event-manager `/slug/:slug/guest`,
     * JSS#1175): served for every visibility, since the /join link is how a
     * producer invites guests to a private event. The viewer lookup
     * (`/slug/:slug`) answers 404 for a private event to anyone outside its org.
     */
    async eventBySlug(slug) {
        const row = await this.json(`/events/slug/${encodeURIComponent(slug)}/guest`);
        const config = (row.config ?? {});
        return {
            id: String(row.id),
            name: String(row.name ?? ''),
            slug: String(row.slug ?? slug),
            status: String(row.status ?? ''),
            recordParticipantIsos: Boolean(row.recordParticipantIsos ?? row.record_participant_isos ?? config.recordParticipantIsos ?? false),
        };
    }
    /** Ask to join. The answer's `guestToken` is the guest's credential from here on. */
    requestJoin(eventId, userId, displayName) {
        return this.json(`/events/${eventId}/participants`, {
            method: 'POST',
            body: JSON.stringify({ userId, displayName }),
        });
    }
    /** The guest's own row. */
    participant(eventId, participantId, guestToken) {
        return this.json(`/events/${eventId}/participants/${participantId}`, { headers: guestHeaders(guestToken) });
    }
    /**
     * The guest's status and slot; null once its row is gone (404). Throws on
     * anything else, a 401 included.
     */
    async participantState(eventId, participantId, guestToken) {
        try {
            const row = await this.participant(eventId, participantId, guestToken);
            return { status: String(row.status), slot: slotOf(row.slot) };
        }
        catch (err) {
            if (err instanceof HttpError && err.status === 404)
                return null;
            throw err;
        }
    }
    riftToken(eventId, participantId, guestToken) {
        return this.json(`/events/${eventId}/participants/${participantId}/rift-token`, {
            method: 'POST',
            headers: guestHeaders(guestToken),
        });
    }
    setMedia(eventId, participantId, guestToken, media) {
        return this.json(`/events/${eventId}/participants/${participantId}/camera`, {
            method: 'PATCH',
            headers: guestHeaders(guestToken),
            body: JSON.stringify(media),
        });
    }
    leave(eventId, participantId, guestToken) {
        return this.json(`/events/${eventId}/participants/${participantId}`, {
            method: 'DELETE',
            headers: guestHeaders(guestToken),
        });
    }
}
/** A row's `slot`: a positive integer, else null (absent, null, or anything malformed). */
function slotOf(v) {
    return typeof v === 'number' && Number.isInteger(v) && v > 0 ? v : null;
}
/** Decode (not verify — the gateway verifies) a JWT's payload. */
export function decodeTokenClaims(token) {
    const part = token.split('.')[1];
    if (!part)
        return {};
    try {
        const b64 = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=');
        const json = new TextDecoder().decode(Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)));
        return JSON.parse(json);
    }
    catch {
        return {};
    }
}
const SESSION_LOST = 'Your place in this event is no longer valid. Please join again.';
/** Removed by someone other than this guest: a producer, the event ending, or the presence timeout after a long drop. */
const REMOVED = 'The producer ended your session, or the event is over.';
/** The gateway kept refusing the Hello although event-manager says the guest is admitted. */
const REFUSED = 'The gateway would not accept your connection. Please join again.';
/** 4xx statuses on the guest's own routes that mean it is out, not that the call was unlucky. */
const OFF_AIR_STATUSES = new Set([400, 401, 403, 404, 409]);
export class JoinController {
    o;
    s = {
        phase: 'loading',
        event: null,
        participantId: null,
        slot: null,
        noticeAcknowledged: false,
        error: null,
    };
    pollMs;
    sleep;
    cancelled = false;
    lastToken = null;
    /** The guest's credential from the join; kept beside `participantId`, never in the published state. */
    guestToken = null;
    /** Cuts the current poll interval short (`pollNow`). */
    wake = null;
    constructor(o) {
        this.o = o;
        this.pollMs = o.pollMs ?? 2_000;
        this.sleep = o.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
    }
    get state() {
        return this.s;
    }
    set(patch) {
        this.s = { ...this.s, ...patch };
        this.o.onChange?.(this.s);
    }
    async load() {
        try {
            const event = await this.o.api.eventBySlug(this.o.slug);
            this.set({ phase: 'ready', event });
        }
        catch (err) {
            this.set({ phase: 'error', error: err instanceof HttpError && err.status === 404 ? 'Event not found' : String(err) });
        }
    }
    acknowledgeNotice() {
        this.set({ noticeAcknowledged: true });
        if (this.s.phase === 'notice')
            this.set({ phase: 'admitted' });
    }
    /** The join button. Refuses while an owed recording notice is unacknowledged. */
    async join(userId, displayName) {
        const ev = this.s.event;
        if (!ev || this.s.phase !== 'ready')
            return;
        if (ev.recordParticipantIsos && !this.s.noticeAcknowledged) {
            this.set({ error: 'Please acknowledge that this event records participants.' });
            return;
        }
        this.set({ phase: 'requesting', error: null });
        try {
            const row = await this.o.api.requestJoin(ev.id, userId, displayName);
            if (!row.guestToken)
                throw new Error('the server issued no guest token');
            this.guestToken = row.guestToken;
            this.set({ phase: 'waiting', participantId: row.id });
        }
        catch (err) {
            this.set({ phase: 'ready', error: `Could not request to join: ${err instanceof Error ? err.message : err}` });
            return;
        }
        await this.waitForAdmission();
    }
    /** Sleep one poll interval, or less if `pollNow` is called meanwhile. */
    nap() {
        return new Promise((resolve) => {
            const done = () => {
                if (this.wake === done)
                    this.wake = null;
                resolve();
            };
            this.wake = done;
            void this.sleep(this.pollMs).then(done);
        });
    }
    /**
     * Re-read the guest's row now instead of at the next poll. The caller uses
     * it when the gateway's keyframe request says the guest was just routed
     * onto a slot, so "You're live" shows without waiting out the interval.
     */
    pollNow() {
        this.wake?.();
    }
    async waitForAdmission() {
        const ev = this.s.event;
        const pid = this.s.participantId;
        const guestToken = this.guestToken;
        while (!this.cancelled) {
            let row;
            try {
                row = await this.o.api.participantState(ev.id, pid, guestToken);
            }
            catch (err) {
                if (this.cancelled)
                    return;
                if (isGuestAuthError(err)) {
                    this.set({ phase: 'ended', error: SESSION_LOST });
                    return;
                }
                await this.nap(); // transient; keep waiting
                continue;
            }
            if (this.cancelled)
                return;
            if (row === null) {
                this.set({ phase: 'ended', error: 'Your request is no longer on the list.' });
                return;
            }
            if (row.status === 'approved') {
                try {
                    const tok = await this.fetchToken();
                    if (this.cancelled)
                        return;
                    const recordIso = tok.recordIso === true || decodeTokenClaims(tok.token).record_iso === true;
                    this.set({ phase: recordIso && !this.s.noticeAcknowledged ? 'notice' : 'admitted', slot: row.slot, error: null });
                    return;
                }
                catch (err) {
                    if (err instanceof HttpError && err.status === 409) {
                        this.set({ phase: 'ended', error: `This event isn't set up for guests yet (${err.message}).` });
                        return;
                    }
                    // 400 races the admission; anything else may be transient. Poll
                    // again — but say so, rather than spin silently (JBS#2152).
                    this.set({ error: `Admitted, but no connection pass yet: ${err instanceof Error ? err.message : err}` });
                }
            }
            else if (row.status !== 'requested') {
                // `removed` (rejected, or removed before admission), or a status this
                // client does not know: either way it will not be admitted.
                this.set({ phase: 'ended', error: null });
                return;
            }
            await this.nap();
        }
    }
    /** Latest token (the RIFT client calls this on connect and on refresh). */
    async fetchToken() {
        const ev = this.s.event;
        const pid = this.s.participantId;
        if (!ev || !pid || !this.guestToken)
            throw new Error('not joined');
        this.lastToken = await this.o.api.riftToken(ev.id, pid, this.guestToken);
        return this.lastToken;
    }
    /** Tell the control plane the guest's camera/mic state (no-op before joining). */
    async setMedia(media) {
        const ev = this.s.event;
        const pid = this.s.participantId;
        if (!ev || !pid || !this.guestToken)
            return;
        await this.o.api.setMedia(ev.id, pid, this.guestToken, media);
    }
    get token() {
        return this.lastToken;
    }
    /**
     * Called by the RIFT client when a token fetch fails: true when the guest
     * is out (not admitted any more, its token refused, its row gone, a setup
     * fault). A network failure, a 5xx, a 408 or a 429 is not: the client keeps
     * retrying, because losing the network is not a Leave.
     */
    static isOffAir(err) {
        return err instanceof HttpError && OFF_AIR_STATUSES.has(err.status);
    }
    /**
     * While admitted, keep polling the guest's own row. Its `slot` says whether
     * the guest is live; a producer removing them, or the presence timeout
     * after a long drop, must end the session even though the RIFT token is
     * still valid. A failed poll changes nothing: the guest stays connected.
     * Resolves once the guest is out (phase 'ended') or the controller stops.
     */
    async watchAdmitted() {
        const ev = this.s.event;
        const pid = this.s.participantId;
        const guestToken = this.guestToken;
        if (!ev || !pid || !guestToken)
            return;
        while (!this.cancelled && (this.s.phase === 'admitted' || this.s.phase === 'notice')) {
            await this.nap();
            if (this.cancelled)
                return;
            let row;
            try {
                row = await this.o.api.participantState(ev.id, pid, guestToken);
            }
            catch (err) {
                if (this.cancelled)
                    return;
                if (isGuestAuthError(err)) {
                    this.set({ phase: 'ended', slot: null, error: SESSION_LOST });
                    return;
                }
                continue; // transient: stay connected, keep the slot we last saw
            }
            if (this.cancelled)
                return;
            if (row === null || row.status !== 'approved') {
                this.set({ phase: 'ended', slot: null, error: REMOVED });
                return;
            }
            if (row.slot !== this.s.slot)
                this.set({ slot: row.slot });
        }
    }
    /**
     * One read of the guest's row, for the RIFT client after the gateway or the
     * network ended its session (the gateway closes a removed guest with 4003,
     * which the browser client cannot see). True while the guest is admitted:
     * redial. False once they are out (removed, row gone, guest token refused),
     * which ends the session. Throws when there is no answer (network, 5xx), and
     * the caller redials as usual, because losing the network is not a Leave.
     */
    async confirmAdmitted() {
        const ev = this.s.event;
        const pid = this.s.participantId;
        const guestToken = this.guestToken;
        if (this.cancelled || !ev || !pid || !guestToken)
            return false;
        let row;
        try {
            row = await this.o.api.participantState(ev.id, pid, guestToken);
        }
        catch (err) {
            if (!isGuestAuthError(err))
                throw err;
            row = null;
        }
        if (this.cancelled)
            return false;
        if (row === null || row.status !== 'approved') {
            this.markEnded(REMOVED);
            return false;
        }
        if (row.slot !== this.s.slot)
            this.set({ slot: row.slot });
        return true;
    }
    markEnded(reason = null) {
        this.set({ phase: 'ended', slot: null, error: reason });
    }
    /** The RIFT client gave up: the gateway kept refusing the Hello. */
    markRefused() {
        this.markEnded(REFUSED);
    }
    /**
     * Stop polling without leaving: the page unmounting, a reload or a closed
     * tab. The participant stays admitted (and on its slot) until the presence
     * timeout removes it (contracts v2 §7).
     */
    dispose() {
        this.cancelled = true;
        this.pollNow();
    }
    /** The Leave button: removes the participant at once. */
    async leave() {
        this.dispose();
        const ev = this.s.event;
        const pid = this.s.participantId;
        if (ev && pid && this.guestToken)
            await this.o.api.leave(ev.id, pid, this.guestToken).catch(() => undefined);
        this.set({ phase: 'ended', slot: null, error: null });
    }
}
//# sourceMappingURL=join.js.map